import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/esm/typeof";
import _inheritsLoose from "@babel/runtime/helpers/esm/inheritsLoose";
var _excluded = ["children", "onOpen", "onClose", "triggered", "offset", "placement", "popperJSOptions", "shouldFocusTriggered", "shouldUseYieldCallback"];
import { createPopper } from '@popperjs/core';
import PropTypes from 'prop-types';
import React, { Children, cloneElement, Component, createRef } from 'react';
import CustomPropTypes from '../../util/custom-prop-types';
import { Trigger } from '../Trigger/Trigger';
import { TriggerButton } from '../TriggerButton/TriggerButton';

/**
 * Make sure the node has a ref, creating one if it does not exist.
 * Existing refs can be from React.createRef() or of callback form.
 *
 * @param node {node} The node to augment
 * @param callback {function} A callback called with the ref value
 * @return {node} The augmented node
 */
var augmentWithRef = function augmentWithRef(node, callback) {
  if (typeof node.ref === 'function') {
    // Callback ref exists
    var callbackRef = node.ref;
    var _ref = function _ref(current) {
      callback({
        current: current
      });
      callbackRef(current);
    };
    return /*#__PURE__*/cloneElement(node, {
      ref: _ref
    });
  }
  if (node.ref) {
    // React.createRef() exists
    callback(node.ref);
    return node;
  }
  // No ref exists, create a new one
  var ref = /*#__PURE__*/createRef();
  callback(ref);
  return /*#__PURE__*/cloneElement(node, {
    ref: ref
  });
};

/**
 * `Popper` is a `Trigger` that enhances the `triggered` content with positioning from
 * the [Popper.js](https://popper.js.org/) library.
 *
 * **IE11 Support:**
 * Popper.js requires [polyfills](https://popper.js.org/docs/v2/browser-support/#ie11) to support
 * IE11. The polyfills are not provided by Popper.js or Constellation;
 * your application must install these.
 *
 * @related ['DropdownPopper', 'MenuPopper', 'TooltipPopper', 'Trigger']
 * @since 7.0.0
 */
export var Popper = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Popper, _Component);
  function Popper(props) {
    var _this;
    _this = _Component.call(this, props) || this;
    _this.onOpen = function () {
      _this.updatePopperJS();
      _this.props.onOpen();
    };
    _this.onClose = function () {
      _this.updatePopperJS();
      _this.props.onClose();
    };
    _this.destroyPopperJS = function () {
      if (_this.popper) {
        _this.popper.destroy();
        _this.popper = null;
      }
    };
    _this.getPopperJSReference = function () {
      if (_typeof(_this.props.popperJSReference) === 'object') {
        return _this.props.popperJSReference.current;
      }
      return _this.triggerRef.current;
    };
    /**
     * Check for popper/trigger/triggered existance and initialize/destroy popper accordingly.
     */
    _this.updatePopperJS = function () {
      if (!_this.popper && _this.getPopperJSReference() && _this.triggeredRef.current) {
        _this.initPopperJS();
      } else if (_this.popper && (!_this.triggerRef.current || !_this.triggeredRef.current)) {
        _this.destroyPopperJS();
      }
    };
    _this.initPopperJS = function () {
      var defaultOptions = {
        placement: _this.props.placement,
        modifiers: [{
          name: 'offset',
          options: {
            offset: _this.props.offset
          }
        }],
        onFirstUpdate: function onFirstUpdate() {
          if (_this.props.shouldFocusTriggered) {
            _this.triggeredRef.current.focus();
          }
        }
      };
      if (_this.arrowRef.current) {
        defaultOptions.modifiers.push({
          name: 'arrow',
          options: {
            element: _this.arrowRef.current
          }
        });
      }
      var options;
      if (typeof _this.props.popperJSOptions === 'function') {
        options = _this.props.popperJSOptions(defaultOptions);
      } else {
        options = Object.assign({}, defaultOptions, _this.props.popperJSOptions);
      }
      _this.popper = createPopper(_this.getPopperJSReference(), _this.triggeredRef.current, options);
    };
    /**
     * A render function that will be passed to `Trigger` to render the triggered content.
     */
    _this.renderTriggered = function (spreadProps, close) {
      var triggered = _this.props.triggered;
      var triggeredProps = Object.assign({
        arrowRef: _this.arrowRef
      }, spreadProps);
      if (_this.triggerRef) {
        triggeredProps.triggerRef = _this.triggerRef;
      }
      if (_this.triggeredRef) {
        triggeredProps.ref = _this.triggeredRef;
      }
      var triggeredContent;
      if (typeof triggered === 'function') {
        triggeredContent = triggered(triggeredProps, close);
      } else {
        triggeredContent = /*#__PURE__*/cloneElement(Children.only(triggered), triggeredProps);
      }

      // Capture or create the ref to be used with popper.js
      if (!_this.triggeredRef) {
        triggeredContent = augmentWithRef(triggeredContent, function (ref) {
          _this.triggeredRef = ref;
        });
      }
      return triggeredContent;
    };
    _this.arrowRef = /*#__PURE__*/createRef();
    return _this;
  }
  var _proto = Popper.prototype;
  _proto.componentDidMount = function componentDidMount() {
    var _this2 = this;
    this.updatePopperJS();
    // Wait for the popperJSReference ref to initialize
    // TODO: Fix this in refactor for 9x
    setTimeout(function () {
      if (_this2.props.popperJSReference === 'object') {
        _this2.updatePopperJS();
      }
    }, 100);
  };
  _proto.componentDidUpdate = function componentDidUpdate() {
    this.updatePopperJS();
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    this.destroyPopperJS();
  };
  _proto.render = function render() {
    var _this3 = this;
    var _this$props = this.props,
      children = _this$props.children,
      onOpen = _this$props.onOpen,
      onClose = _this$props.onClose,
      triggered = _this$props.triggered,
      offset = _this$props.offset,
      placement = _this$props.placement,
      popperJSOptions = _this$props.popperJSOptions,
      shouldFocusTriggered = _this$props.shouldFocusTriggered,
      shouldUseYieldCallback = _this$props.shouldUseYieldCallback,
      rest = _objectWithoutProperties(_this$props, _excluded);
    var triggerProps = {};
    if (this.triggerRef) {
      triggerProps.ref = this.triggerRef;
    }
    var triggerContent;
    if (typeof children === 'function') {
      triggerContent = children(triggerProps);
    } else {
      triggerContent = /*#__PURE__*/cloneElement(Children.only(children), triggerProps);
    }

    // Capture or create the ref to be used with popper.js
    if (!this.triggerRef) {
      triggerContent = augmentWithRef(triggerContent, function (ref) {
        _this3.triggerRef = ref;
      });
    }
    return /*#__PURE__*/React.createElement(Trigger, _extends({
      triggered: this.renderTriggered,
      onOpen: this.onOpen,
      onClose: this.onClose,
      shouldUseYieldCallback: shouldUseYieldCallback
    }, rest), triggerContent);
  };
  return Popper;
}(Component);
Popper.displayName = 'Popper';
Popper.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The `Trigger` `defaultOpen` prop.
   */
  defaultOpen: PropTypes.bool,
  /**
   * The `Trigger` `children` content.
   * The trigger will be passed `ref` for positioning with the [Popper.js](https://popper.js.org/)
   * library.
   * **The node must be a single element capable of accepting props/refs (no fragments).**
   */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  /**
   * The `Trigger` `isOpen` prop.
   *
   * @since 8.24.0
   */
  isOpen: PropTypes.bool,
  /**
   * The `Trigger` `onOpen` handler.
   */
  onOpen: PropTypes.func,
  /**
   * The `Trigger` `onClose` hander.
   */
  onClose: PropTypes.func,
  /**
   * The [`offset`](https://popper.js.org/docs/v2/modifiers/offset/#offset-1) option for the
   * [`Offset`](https://popper.js.org/docs/v2/modifiers/offset/) modifier.
   */
  offset: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  /**
   * Sets the [`placement`](https://popper.js.org/docs/v2/constructors/#placement) option.
   */
  placement: CustomPropTypes.popperPlacement,
  /**
   * An Popper.js [`options`](https://popper.js.org/docs/v2/constructors/#options) object that
   * will be spread into the default options, or a function that receives the default options
   * as the only parameter and returns an options object.
   */
  popperJSOptions: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  /**
   * By default, the trigger will be used as the reference point when positioning the triggered
   * content. To override this behavior, you can pass the ref object of another node you would
   * like to position the content relative to.
   *
   * @since 8.28.0
   */
  // eslint-disable-next-line zillow/react/forbid-prop-types
  popperJSReference: PropTypes.object,
  /**
   * Focus the `triggered` node once the popover is positioned.
   */
  shouldFocusTriggered: PropTypes.bool,
  /**
   * The `Trigger` `triggered` content.
   *
   * When `triggered` is a node, the content will be passed `ref` and `arrowRef` for positioning
   * with the [Popper.js](https://popper.js.org/) library.
   *
   * When `triggered` is a function, the function will provide `prop` that should be spread into
   * the content, and a `close` function that can be called to close the popper.
   *
   * **Trigger content must be a single element capable of accepting props/refs (no fragments).**
   */
  triggered: PropTypes.oneOfType([PropTypes.element, PropTypes.func]).isRequired,
  /**
   * Close the trigger when the user scrolls away from the trigger node.
   */
  shouldCloseOnViewportLeave: PropTypes.bool,
  /**
   * When `true`, the `onOpen` and `onClose` handlers will be wrapped in `yieldCallback`
   */
  shouldUseYieldCallback: PropTypes.bool
} : {};
Popper.defaultProps = {
  children: /*#__PURE__*/React.createElement(TriggerButton, null),
  defaultOpen: false,
  onClose: function onClose() {},
  onOpen: function onOpen() {},
  offset: function offset(_ref2) {
    var placement = _ref2.placement;
    if (placement.indexOf('start') >= 0) {
      return [-20, 20];
    }
    if (placement.indexOf('end') >= 0) {
      return [20, 20];
    }
    return [0, 20];
  },
  placement: 'bottom-start',
  popperJSOptions: {},
  shouldFocusTriggered: true,
  shouldCloseOnViewportLeave: false,
  shouldUseYieldCallback: true
};